import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Descriptions,
  Empty,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import {
  FIRESTORE_PRODUCTS_STOCK_TABLE,
  FIRESTORE_RECONCILIATION_RUNS_COLLECTION,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import dayjs from "dayjs";
import urls from "urls";
import Loading from "components/shared-components/Loading";
import Flex from "components/shared-components/Flex";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import useIsMobile from "hooks/useIsMobile";

const { Title, Text } = Typography;
const { Search } = Input;

const ReconciliationDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { runId } = useParams();
  const isMobileView = useIsMobile();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [fixing, setFixing] = useState(false);
  const [fixingIds, setFixingIds] = useState(new Set());
  const [fixSummaryVisible, setFixSummaryVisible] = useState(false);
  const [fixSummary, setFixSummary] = useState({ success: [], failed: [] });
  const { products } = useProducts();

  useEffect(() => {
    const fetchRun = async () => {
      if (!runId) return;

      try {
        const runDocRef = doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_RECONCILIATION_RUNS_COLLECTION,
          runId,
        );
        const runDoc = await getDoc(runDocRef);

        if (runDoc.exists()) {
          setRun({
            id: runDoc.id,
            ...runDoc.data(),
          });
        }
      } catch (error) {
        console.error("Error fetching reconciliation run:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRun();
  }, [runId]);

  const getProductName = (productId) => {
    const product = products?.find((p) => p.productId === productId || p.id === productId);
    return product?.productName || product?.productNameNL || productId;
  };

  const filteredDiscrepancies = useMemo(() => {
    if (!run?.discrepancies) return [];

    if (!searchTerm) return run.discrepancies;

    const term = searchTerm.toLowerCase();
    return run.discrepancies.filter(
      (d) =>
        d.productId.toLowerCase().includes(term) ||
        getProductName(d.productId).toLowerCase().includes(term) ||
        d.dateId.includes(term),
    );
  }, [run?.discrepancies, searchTerm, products]);

  const getDifferenceBadge = (difference) => {
    if (difference > 0) {
      return (
        <Badge
          count={`+${difference}`}
          style={{ backgroundColor: "#52c41a" }}
          title={t("reconciliation_more_expected")}
        />
      );
    }
    return (
      <Badge count={difference} style={{ backgroundColor: "#f5222d" }} title={t("reconciliation_less_expected")} />
    );
  };

  const getScopeTag = (scope) => {
    if (scope === "week") {
      return <Tag color="blue">{t("reconciliation_scope_week")}</Tag>;
    }
    if (scope === "full") {
      return <Tag color="purple">{t("reconciliation_scope_full")}</Tag>;
    }
    return <Tag>{scope}</Tag>;
  };

  const fixDiscrepancy = async (discrepancy) => {
    const key = `${discrepancy.productId}-${discrepancy.dateId}`;
    setFixingIds((prev) => new Set(prev).add(key));

    try {
      // Fetch current stock data from Firestore
      const stockDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_PRODUCTS_STOCK_TABLE,
        discrepancy.dateId,
      );

      const stockDoc = await getDoc(stockDocRef);

      if (!stockDoc.exists()) {
        throw new Error(t("reconciliation_stock_doc_not_found"));
      }

      const stockData = stockDoc.data();
      const productStockData = stockData.products?.[discrepancy.productId];

      if (!productStockData || typeof productStockData !== "object") {
        throw new Error(t("reconciliation_stock_doc_not_found"));
      }

      // Stock data is an object with properties like currentStock, requestedStock, etc.
      const currentCountedStock = productStockData.countedStock ?? 0;
      const currentRequestedStock = productStockData.requestedStock ?? 0;

      // Check if requestedStock still has the wrong value
      // If someone already fixed it manually, we should not overwrite it
      if (currentRequestedStock !== discrepancy.actual) {
        throw new Error(
          t("reconciliation_already_fixed", {
            current: currentRequestedStock,
            expected: discrepancy.actual,
          }),
        );
      }

      // Set requestedStock to the expected value (from orders)
      // Set currentStock to countedStock - requestedStock
      const newRequestedStock = discrepancy.expected;
      const newCurrentStock = currentCountedStock - discrepancy.expected;

      await updateDoc(stockDocRef, {
        [`products.${discrepancy.productId}.requestedStock`]: newRequestedStock,
        [`products.${discrepancy.productId}.currentStock`]: newCurrentStock,
      });

      return {
        success: true,
        productId: discrepancy.productId,
        dateId: discrepancy.dateId,
        oldValue: discrepancy.actual,
        newValue: discrepancy.expected,
      };
    } catch (error) {
      return {
        success: false,
        productId: discrepancy.productId,
        dateId: discrepancy.dateId,
        error: error.message,
      };
    } finally {
      setFixingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const handleFixSingle = async (discrepancy) => {
    const result = await fixDiscrepancy(discrepancy);

    if (result.success) {
      message.success(t("reconciliation_fix_success"));
      setFixSummary({ success: [result], failed: [] });
      setFixSummaryVisible(true);
    } else {
      message.error(result.error);
      setFixSummary({ success: [], failed: [result] });
      setFixSummaryVisible(true);
    }
  };

  const handleFixAll = async () => {
    setFixing(true);
    const results = { success: [], failed: [] };

    for (const discrepancy of filteredDiscrepancies) {
      const result = await fixDiscrepancy(discrepancy);
      if (result.success) {
        results.success.push(result);
      } else {
        results.failed.push(result);
      }
    }

    setFixing(false);
    setFixSummary(results);
    setFixSummaryVisible(true);

    if (results.success.length > 0) {
      message.success(
        t("reconciliation_fix_bulk_complete", {
          success: results.success.length,
          total: filteredDiscrepancies.length,
        }),
      );
    }
  };

  const discrepancyColumns = [
    {
      title: t("product"),
      key: "product",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{getProductName(record.productId)}</Text>
          <Text type="secondary" copyable={{ text: record.productId }}>
            {record.productId}
          </Text>
          {isMobileView && (
            <>
              <Space size={4} wrap>
                <Tag>{record.dateId}</Tag>
                <Text type="secondary">{dayjs(record.deliveryDate).format("DD-MM-YYYY")}</Text>
              </Space>
              <Text>
                {t("reconciliation_actual")}: {record.actual} → {t("reconciliation_expected")}:{" "}
                <Text strong>{record.expected}</Text>
              </Text>
            </>
          )}
        </Space>
      ),
    },
    {
      title: t("delivery_date"),
      dataIndex: "deliveryDate",
      key: "deliveryDate",
      responsive: ["lg"],
      render: (date) => dayjs(date).format("DD-MM-YYYY"),
    },
    {
      title: t("packing_date_id"),
      dataIndex: "dateId",
      key: "dateId",
      responsive: ["lg"],
      render: (dateId) => <Tag>{dateId}</Tag>,
    },
    {
      title: t("reconciliation_expected"),
      dataIndex: "expected",
      key: "expected",
      responsive: ["lg"],
      align: "center",
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: t("reconciliation_actual"),
      dataIndex: "actual",
      key: "actual",
      responsive: ["lg"],
      align: "center",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: t("reconciliation_difference"),
      dataIndex: "difference",
      key: "difference",
      align: "center",
      render: (difference) => (
        <Tooltip title={difference > 0 ? t("reconciliation_more_expected") : t("reconciliation_less_expected")}>
          {getDifferenceBadge(difference)}
        </Tooltip>
      ),
    },
    {
      title: t("actions"),
      key: "actions",
      align: "center",
      render: (_, record) => {
        const key = `${record.productId}-${record.dateId}`;
        const isFixing = fixingIds.has(key);

        return (
          <Popconfirm
            title={t("reconciliation_fix_confirm_title")}
            description={t("reconciliation_fix_confirm_description", {
              product: getProductName(record.productId),
              dateId: record.dateId,
              from: record.actual,
              to: record.expected,
            })}
            onConfirm={() => handleFixSingle(record)}
            okText={t("yes")}
            cancelText={t("no")}
            disabled={isFixing}
          >
            <Button
              type="primary"
              size="small"
              icon={<SyncOutlined spin={isFixing} />}
              loading={isFixing}
              disabled={isFixing}
            >
              {!isMobileView && t("reconciliation_fix")}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const handleBack = () => {
    navigate(urls.reconciliationLog);
  };

  if (loading) {
    return <Loading />;
  }

  if (!run) {
    return (
      <Card>
        <Empty description={t("reconciliation_run_not_found")} />
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <Button type="primary" onClick={handleBack} icon={<ArrowLeftOutlined />}>
            {t("back_to_list")}
          </Button>
        </div>
      </Card>
    );
  }

  const hasDiscrepancies = run.discrepancyCount > 0;

  return (
    <div>
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={handleBack} className="mb-3">
        {t("back_to_reconciliation_log")}
      </Button>

      <Card className="mb-3">
        <Flex alignItems="center" justifyContent="between" mobileFlex={false}>
          <Space direction="vertical" size={0}>
            <Title level={4} className="mb-0">
              <HistoryOutlined style={{ marginRight: 8 }} />
              {t("reconciliation_run_details")}
            </Title>
            <Text type="secondary">{run.runId}</Text>
          </Space>
          {getScopeTag(run.scope)}
        </Flex>

        <Descriptions
          bordered
          size={isMobileView ? "small" : "default"}
          className="mt-4"
          column={{ xxl: 4, xl: 4, lg: 3, md: 2, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label={t("reconciliation_run_date")}>
            {dayjs(run.timestamp * 1000).format("DD-MM-YYYY HH:mm:ss")}
          </Descriptions.Item>
          <Descriptions.Item label={t("reconciliation_horizon")}>
            {run.horizonDays + 1} {t("reconciliation_days")}
          </Descriptions.Item>
          <Descriptions.Item label={t("reconciliation_dates_checked")}>{run.datesChecked}</Descriptions.Item>
          <Descriptions.Item label={t("reconciliation_orders_scanned")}>{run.ordersScanned}</Descriptions.Item>
        </Descriptions>

        <div className="mt-4">
          <Space size="large" wrap>
            <Statistic
              title={t("reconciliation_total_discrepancies")}
              value={run.discrepancyCount}
              valueStyle={{
                color: hasDiscrepancies ? "#f5222d" : "#52c41a",
              }}
              prefix={hasDiscrepancies ? <WarningOutlined /> : <CheckCircleOutlined />}
            />
            {hasDiscrepancies && (
              <Statistic
                title={t("reconciliation_affected_dates")}
                value={new Set(run.discrepancies.map((d) => d.dateId)).size}
              />
            )}
            {hasDiscrepancies && (
              <Statistic
                title={t("reconciliation_affected_products")}
                value={new Set(run.discrepancies.map((d) => d.productId)).size}
              />
            )}
          </Space>
        </div>
      </Card>

      <Card>
        <Title level={5} className="mb-3">
          {hasDiscrepancies ? (
            <Space>
              <ExclamationCircleOutlined style={{ color: "#f5222d" }} />
              {t("reconciliation_discrepancies_title")}
            </Space>
          ) : (
            <Space>
              <CheckCircleOutlined style={{ color: "#52c41a" }} />
              {t("reconciliation_no_discrepancies_title")}
            </Space>
          )}
        </Title>

        {hasDiscrepancies ? (
          <>
            <Flex alignItems="center" justifyContent="between" mobileFlex={false} className="mb-3">
              <Search
                placeholder={t("search_products_or_dates")}
                allowClear
                onSearch={setSearchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: "100%", maxWidth: isMobileView ? undefined : 300 }}
                className="mb-2 mb-md-0"
              />
              <Popconfirm
                title={t("reconciliation_fix_all_confirm_title")}
                description={t("reconciliation_fix_all_confirm_description", {
                  count: filteredDiscrepancies.length,
                })}
                onConfirm={handleFixAll}
                okText={t("yes")}
                cancelText={t("no")}
                disabled={fixing}
              >
                <Button type="primary" icon={<SyncOutlined spin={fixing} />} loading={fixing} disabled={fixing}>
                  {t("reconciliation_fix_all")}
                </Button>
              </Popconfirm>
            </Flex>

            <Table
              columns={discrepancyColumns}
              dataSource={filteredDiscrepancies}
              rowKey={(record) => `${record.productId}-${record.dateId}`}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `${t("total")} ${total} ${t("discrepancies")}`,
              }}
              scroll={isMobileView ? undefined : { x: 800 }}
              summary={() => {
                const totalDifference = run.discrepancies.reduce((sum, d) => sum + d.difference, 0);
                const differenceCell = (
                  <Table.Summary.Cell align="center">
                    <Text strong type={totalDifference !== 0 ? "danger" : "success"}>
                      {totalDifference > 0 ? "+" : ""}
                      {totalDifference}
                    </Text>
                  </Table.Summary.Cell>
                );
                if (isMobileView) {
                  return (
                    <Table.Summary.Row>
                      <Table.Summary.Cell>
                        <Text strong>{t("reconciliation_total_difference")}</Text>
                      </Table.Summary.Cell>
                      {differenceCell}
                    </Table.Summary.Row>
                  );
                }
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={3}>
                      <Text strong>{t("reconciliation_total_difference")}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="center">
                      <Text strong>{run.discrepancies.reduce((sum, d) => sum + d.expected, 0)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="center">
                      <Text strong>{run.discrepancies.reduce((sum, d) => sum + d.actual, 0)}</Text>
                    </Table.Summary.Cell>
                    {differenceCell}
                  </Table.Summary.Row>
                );
              }}
            />
          </>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" align="center">
                <CheckCircleOutlined style={{ fontSize: 48, color: "#52c41a" }} />
                <Text type="secondary">{t("reconciliation_all_stock_matches")}</Text>
              </Space>
            }
          />
        )}
      </Card>

      <Modal
        title={t("reconciliation_fix_summary")}
        open={fixSummaryVisible}
        onCancel={() => setFixSummaryVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setFixSummaryVisible(false)}>
            {t("close")}
          </Button>,
        ]}
        width={700}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {fixSummary.success.length > 0 && (
            <div>
              <Title level={5}>
                <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />
                {t("reconciliation_fix_success_count", { count: fixSummary.success.length })}
              </Title>
              <Table
                dataSource={fixSummary.success}
                rowKey={(record) => `${record.productId}-${record.dateId}`}
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
                columns={[
                  {
                    title: t("product"),
                    dataIndex: "productId",
                    key: "productId",
                    render: (productId) => (
                      <Space direction="vertical" size={0}>
                        <Text strong>{getProductName(productId)}</Text>
                        <Text type="secondary">{productId}</Text>
                      </Space>
                    ),
                  },
                  {
                    title: t("packing_date_id"),
                    dataIndex: "dateId",
                    key: "dateId",
                    render: (dateId) => <Tag>{dateId}</Tag>,
                  },
                  {
                    title: t("reconciliation_old_value"),
                    dataIndex: "oldValue",
                    key: "oldValue",
                    align: "center",
                  },
                  {
                    title: t("reconciliation_new_value"),
                    dataIndex: "newValue",
                    key: "newValue",
                    align: "center",
                    render: (value) => (
                      <Text strong style={{ color: "#52c41a" }}>
                        {value}
                      </Text>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {fixSummary.failed.length > 0 && (
            <div>
              <Title level={5}>
                <CloseCircleOutlined style={{ color: "#f5222d", marginRight: 8 }} />
                {t("reconciliation_fix_failed_count", { count: fixSummary.failed.length })}
              </Title>
              <Table
                dataSource={fixSummary.failed}
                rowKey={(record) => `${record.productId}-${record.dateId}`}
                pagination={false}
                size="small"
                scroll={{ x: "max-content" }}
                columns={[
                  {
                    title: t("product"),
                    dataIndex: "productId",
                    key: "productId",
                    render: (productId) => (
                      <Space direction="vertical" size={0}>
                        <Text strong>{getProductName(productId)}</Text>
                        <Text type="secondary">{productId}</Text>
                      </Space>
                    ),
                  },
                  {
                    title: t("packing_date_id"),
                    dataIndex: "dateId",
                    key: "dateId",
                    render: (dateId) => <Tag>{dateId}</Tag>,
                  },
                  {
                    title: t("reconciliation_error"),
                    dataIndex: "error",
                    key: "error",
                    render: (error) => <Text type="danger">{error}</Text>,
                  },
                ]}
              />
            </div>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default ReconciliationDetail;
